// frontend/src/utils/lazyWithPreload.js
//
// React.lazy with two additions:
//
//  - preload(): start the chunk download early, and - the part that matters -
//    once the module has loaded, the component renders it DIRECTLY: no
//    Suspense round trip, no fallback frame, nothing that can suspend.
//  - a FAILED import is retryable. React.lazy caches a rejection for the life
//    of the lazy object, so a stale chunk after a deploy would break the
//    component until a full reload. Here each retry ATTEMPT of a surrounding
//    error boundary that provides LazyAttemptContext gets its own lazy object:
//    React's own re-renders within one attempt reuse it (so a chunk that keeps
//    failing reaches the boundary instead of being re-requested in a loop), and
//    the boundary's retry requests the chunk again. Without a provider the
//    attempt is always 0 - exactly React.lazy's behaviour.
//
// Two users:
//
//  - the /analytics page's feature sections (pages/analytics/), preloaded in
//    parallel with the page's data requests so a section the data turns on
//    does not wait for its code AFTER the data;
//  - every PRERENDERED lazy route in appRoutes.js (#464). A hydrated snapshot must
//    never suspend at the route boundary: a boundary left dehydrated while its
//    chunk downloads is dropped by React 19 the moment any context above it
//    updates (the /pricing reply, a stored consent, the auth check), and
//    PageLoadingFallback is committed over the page. src/index.js therefore
//    awaits `preloadRouteForPath(location.pathname)` before hydrateRoot, so
//    the first render of the route finds the module already loaded here.
//
// Never replace this with a plain React.lazy() on a prerendered route
// (.claude/rules/public-pages.md, "Prerender hydration").

import React, { createContext, lazy, useContext, useState } from "react";

/** The retry attempt of the nearest error boundary that counts them (0 = the first try). */
export const LazyAttemptContext = createContext(0);

/**
 * @param {() => Promise<{default: React.ComponentType}>} factory  a dynamic import()
 * @returns {React.ComponentType & {preload: () => Promise<any>}}
 */
export default function lazyWithPreload(factory) {
  let loaded = null;
  let pending = null;
  const lazies = new Map(); // attempt -> lazy component

  const preload = () => {
    if (!pending) {
      pending = factory().then(
        (mod) => {
          loaded = mod.default;
          return mod;
        },
        (error) => {
          pending = null; // the next attempt imports again
          throw error;
        },
      );
    }
    return pending;
  };

  const lazyFor = (attempt) => {
    if (!lazies.has(attempt)) lazies.set(attempt, lazy(preload));
    return lazies.get(attempt);
  };

  const Preloadable = (props) => {
    const attempt = useContext(LazyAttemptContext);
    // Decided once per mount: switching the element type mid-life would remount it.
    const [Component] = useState(() => loaded || lazyFor(attempt));
    return <Component {...props} />;
  };
  Preloadable.preload = preload;
  return Preloadable;
}
